/**
 * BIP Derivation Purpose Module
 *
 * This module defines Bitcoin Improvement Proposal (BIP) numbers used for derivation purposes.
 * Each BIP number represents a different address derivation standard:
 * - BIP32: Hierarchical Deterministic Wallets (basic)
 * - BIP44: Legacy P2PKH addresses
 * - BIP45: Multi-signature P2SH addresses
 * - BIP48: Multi-signature with different script types
 * - BIP49: Nested SegWit P2SH-P2WPKH addresses
 * - BIP84: Native SegWit P2WPKH addresses
 * - BIP86: Taproot P2TR addresses
 */

// ============================================================================
// 1. CONSTANTS: Derivation Purpose Configuration
// ============================================================================

/**
 * Prefix used for all derivation purpose labels.
 * All labels will be formatted as "BIP_XX" where XX is the BIP number.
 */
export const DERIVATION_PURPOSE_PREFIX = 'BIP' as const;

/**
 * Special purpose value for Electrum Legacy wallets.
 * Electrum Legacy uses m/0'/change/index format without a BIP purpose level.
 */
export const ELECTRUM_LEGACY_PURPOSE = 0 as const;

/**
 * List of all supported BIP numbers for address derivation.
 * This is the single source of truth for valid derivation purposes.
 */
export const DERIVATION_PURPOSE_LIST = [32, 44, 45, 48, 49, 84, 86] as const;

// ============================================================================
// 2. TYPE: Union of Valid BIP Numbers
// ============================================================================

/**
 * Union type of all valid BIP numbers.
 * Equivalent to: 44 | 45 | 48 | 49 | 84 | 86
 */
export type DerivationPurposeNumber = (typeof DERIVATION_PURPOSE_LIST)[number];

// ============================================================================
// 3. HELPER: Convert Number to Label
// ============================================================================

/**
 * Helper function to create a BIP label from a number.
 * @param num - BIP number (e.g., 44)
 * @returns BIP label (e.g., "BIP_44")
 * @internal
 */
const toLabel = (num: DerivationPurposeNumber) => `${DERIVATION_PURPOSE_PREFIX}_${num}` as const;

// ============================================================================
// 4. MAP: Label → Label (for type-safe enum-like behavior)
// ============================================================================

/**
 * Maps BIP labels to themselves for type-safe enum-like behavior.
 * This allows using DerivationPurposeString.BIP_44 in code.
 * Example: { BIP_44: "BIP_44", BIP_49: "BIP_49", ... }
 */
export const DerivationPurposeString = Object.fromEntries(
  DERIVATION_PURPOSE_LIST.map((num) => {
    const label = toLabel(num);
    return [label, label];
  })
) as { [K in `${typeof DERIVATION_PURPOSE_PREFIX}_${DerivationPurposeNumber}`]: K };

// ============================================================================
// 5. MAP: Label → Number
// ============================================================================

/**
 * Maps BIP labels to their corresponding numbers.
 * This is useful for converting from string labels to numeric BIP values.
 * Example: { BIP_44: 44, BIP_49: 49, ... }
 */
export const DerivationPurposeNumberMap = Object.fromEntries(
  DERIVATION_PURPOSE_LIST.map((num) => {
    const label = toLabel(num);
    return [label, num];
  })
) as {
  [K in `${typeof DERIVATION_PURPOSE_PREFIX}_${DerivationPurposeNumber}`]: DerivationPurposeNumber;
};

// ============================================================================
// 6. TYPE: Union of All Labels
// ============================================================================

/**
 * Union type of all derivation purpose labels.
 * Equivalent to: "BIP_44" | "BIP_45" | "BIP_48" | "BIP_49" | "BIP_84" | "BIP_86"
 */
export type DerivationPurposeLabel = keyof typeof DerivationPurposeString;
